import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from '@mui/material';

const DEFAULT_RACE_PRIORITY_LEVEL_CUTOFFS = {
  medium: 1000,
  high: 1940
};

const PRIORITY_META = {
  high: { label: 'High Priority', color: '#876ce9' },
  medium: { label: 'Medium Priority', color: '#6b5fa4' },
  low: { label: 'Low Priority', color: '#b4b3f8' }
};

const getInitialCutoffs = (electionImplementation) => ({
  medium: `${electionImplementation?.racePriorityLevelCutoffs?.medium ?? DEFAULT_RACE_PRIORITY_LEVEL_CUTOFFS.medium}`,
  high: `${electionImplementation?.racePriorityLevelCutoffs?.high ?? DEFAULT_RACE_PRIORITY_LEVEL_CUTOFFS.high}`
});

const parseCutoff = (value) => Number(value);

const getPriorityLevel = (expectedReaders, cutoffs) => {
  if ((Number(expectedReaders) || 0) >= cutoffs.high) return 'high';
  if ((Number(expectedReaders) || 0) >= cutoffs.medium) return 'medium';
  return 'low';
};

const formatNumber = (value) => new Intl.NumberFormat().format(value ?? 0);

export const AdjustPriorityLevelsModal = ({
  open,
  onClose,
  electionKey,
  electionImplementation,
  feathers,
  onSaved
}) => {
  const [cutoffs, setCutoffs] = useState(() => getInitialCutoffs(electionImplementation));
  const [races, setRaces] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCutoffs(getInitialCutoffs(electionImplementation));
    setError(null);
  }, [open, electionImplementation]);

  useEffect(() => {
    if (!open || !feathers || !electionKey) return;

    let cancelled = false;
    const loadRaces = async () => {
      setLoadingPreview(true);
      try {
        const allRaces = [];
        const limit = 50;
        let skip = 0;
        let page;

        do {
          page = await feathers.getService('races').find({
            query: {
              election: electionKey,
              $select: ['_id', 'expectedReaders', 'coverageProgress'],
              coveragePlan: 'auto-coverage',
              $limit: limit,
              $skip: skip
            }
          });
          allRaces.push(...(page?.data || []));
          skip += limit;
        } while ((page?.data || []).length === limit);

        if (!cancelled) setRaces(allRaces);
      } catch (err) {
        console.error('Error loading race priority preview:', err);
        if (!cancelled) setError('Unable to load the candidate distribution preview.');
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    };

    loadRaces();
    return () => {
      cancelled = true;
    };
  }, [open, feathers, electionKey]);

  const parsedCutoffs = useMemo(() => ({
    medium: parseCutoff(cutoffs.medium),
    high: parseCutoff(cutoffs.high)
  }), [cutoffs]);

  const validationError = useMemo(() => {
    if (!Number.isInteger(parsedCutoffs.medium) || parsedCutoffs.medium < 0) {
      return 'Medium priority cutoff must be a non-negative integer.';
    }
    if (!Number.isInteger(parsedCutoffs.high) || parsedCutoffs.high < 0) {
      return 'High priority cutoff must be a non-negative integer.';
    }
    if (parsedCutoffs.medium >= parsedCutoffs.high) {
      return 'High priority cutoff must be greater than medium priority cutoff.';
    }
    return null;
  }, [parsedCutoffs]);

  const distribution = useMemo(() => {
    const next = {
      high: 0,
      medium: 0,
      low: 0
    };

    races.forEach((race) => {
      const numCandidates = race?.coverageProgress?.numCandidates ?? 0;
      const priorityLevel = validationError ? 'low' : getPriorityLevel(race?.expectedReaders, parsedCutoffs);
      next[priorityLevel] += numCandidates;
    });

    const total = next.high + next.medium + next.low;
    return { ...next, total };
  }, [races, parsedCutoffs, validationError]);

  const handleSave = async () => {
    if (validationError) return;
    setSaving(true);
    setError(null);
    try {
      await feathers.getService('election-implementations').patch(electionKey, {
        racePriorityLevelCutoffs: parsedCutoffs
      });
      await onSaved?.();
      onClose();
    } catch (err) {
      console.error('Error saving priority levels:', err);
      setError('Unable to save priority levels. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getPercentage = (count) => {
    if (!distribution.total) return 0;
    return Math.round((count / distribution.total) * 100);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Adjust priority levels</DialogTitle>
      <DialogContent>
        <ModalGrid>
          <LevelsColumn>
            <RangeCard $priority="low">
              <PriorityLabel $priority="low">Low Priority</PriorityLabel>
              <Typography variant="body2">Candidates with minimal reader engagement</Typography>
              <RangeInputs>
                <TextField value="0" disabled size="small" />
                <Typography variant="body2">to</Typography>
                <TextField value={validationError ? '--' : Math.max(parsedCutoffs.medium - 1, 0)} disabled size="small" />
              </RangeInputs>
            </RangeCard>

            <RangeCard $priority="medium">
              <PriorityLabel $priority="medium">Medium Priority</PriorityLabel>
              <Typography variant="body2">Candidates with moderate reader engagement</Typography>
              <RangeInputs>
                <TextField
                  label="From"
                  value={cutoffs.medium}
                  type="number"
                  size="small"
                  onChange={(event) => setCutoffs(current => ({ ...current, medium: event.target.value }))}
                />
                <Typography variant="body2">to</Typography>
                <TextField value={validationError ? '--' : parsedCutoffs.high - 1} disabled size="small" />
              </RangeInputs>
            </RangeCard>

            <RangeCard $priority="high">
              <PriorityLabel $priority="high">High Priority</PriorityLabel>
              <Typography variant="body2">Candidates with high reader engagement</Typography>
              <RangeInputs>
                <TextField
                  label="From"
                  value={cutoffs.high}
                  type="number"
                  size="small"
                  onChange={(event) => setCutoffs(current => ({ ...current, high: event.target.value }))}
                />
                <Typography variant="body2">to</Typography>
                <TextField value="No max" disabled size="small" />
              </RangeInputs>
            </RangeCard>
          </LevelsColumn>

          <DistributionCard>
            <Typography variant="body1" style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
              Candidate Distribution
            </Typography>
            {loadingPreview ? (
              <LoadingPreview>
                <CircularProgress size={32} />
              </LoadingPreview>
            ) : (
              <>
                <Donut
                  $high={getPercentage(distribution.high)}
                  $medium={getPercentage(distribution.medium)}
                >
                  <DonutHole />
                </Donut>
                {['high', 'medium', 'low'].map((priority) => (
                  <DistributionRow key={priority}>
                    <Swatch $priority={priority} />
                    <Typography variant="body1">{PRIORITY_META[priority].label}</Typography>
                    <Typography variant="body1" style={{ marginLeft: 'auto', fontWeight: 'bold' }}>
                      {formatNumber(distribution[priority])} ({getPercentage(distribution[priority])}%)
                    </Typography>
                  </DistributionRow>
                ))}
              </>
            )}
          </DistributionCard>
        </ModalGrid>
        {(validationError || error) && (
          <Typography variant="body2" color="error" style={{ marginTop: '16px' }}>
            {validationError || error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={Boolean(validationError) || saving || loadingPreview}>
          {saving ? 'Saving...' : 'Save priority levels'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: 1.35fr 1fr;
  gap: 24px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const LevelsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RangeCard = styled.div`
  border: 1px solid #E5E8EF;
  border-left: 4px solid ${props => PRIORITY_META[props.$priority].color};
  border-radius: 8px;
  padding: 16px;
  position: relative;
`;

const PriorityLabel = styled(Typography)`
  && {
    color: ${props => PRIORITY_META[props.$priority].color};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1px;
    margin-bottom: 8px;
    text-transform: uppercase;
  }
`;

const RangeInputs = styled.div`
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: 92px auto 92px;
  justify-content: end;
  margin-top: 12px;
`;

const DistributionCard = styled.div`
  border: 1px solid #E5E8EF;
  border-radius: 8px;
  padding: 24px;
`;

const LoadingPreview = styled.div`
  align-items: center;
  display: flex;
  height: 260px;
  justify-content: center;
`;

const Donut = styled.div`
  align-items: center;
  background: conic-gradient(
    ${PRIORITY_META.high.color} 0 ${props => props.$high}%,
    ${PRIORITY_META.medium.color} ${props => props.$high}% ${props => props.$high + props.$medium}%,
    ${PRIORITY_META.low.color} ${props => props.$high + props.$medium}% 100%
  );
  border-radius: 50%;
  display: flex;
  height: 176px;
  justify-content: center;
  margin: 24px auto;
  width: 176px;
`;

const DonutHole = styled.div`
  background: #FFFFFF;
  border-radius: 50%;
  height: 104px;
  width: 104px;
`;

const DistributionRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  padding: 10px 0;
`;

const Swatch = styled.div`
  background: ${props => PRIORITY_META[props.$priority].color};
  border-radius: 2px;
  height: 12px;
  width: 12px;
`;
